import { AxiosError } from "axios";
import Vue from "vue";
import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";
import { Integrations } from "@sentry/tracing";
import store from "@/store";
import { HttpStatusCode } from "@/api/types";
import { RpcError, StatusCode } from "grpc-web";

export const setSentry = (): void => {
  const env = process.env["NODE_ENV"];
  if (env === "development") return;

  Sentry.init({
    dsn:
      "https://612ff499541643bda222cc991cdd9f94@o405964.ingest.sentry.io/5419380",
    environment: env,
    integrations: [
      new VueIntegration({
        Vue,
        tracing: true
      }),
      new Integrations.BrowserTracing()
    ],
    tracesSampleRate: 1,
    beforeSend(event, hint) {
      if (isSkipApiError(hint)) {
        return null;
      }

      if (isSkipGrpcError(hint)) {
        return null;
      }

      return event;
    },
    ignoreErrors: ["Network Error"],
    blacklistUrls: [
      // NOTE: Chrome拡張機能のエラーは除外する
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /extensions\//i
    ]
  });
};

const isSkipApiError = (hint: Sentry.EventHint | undefined) => {
  const isApiError =
    hint?.originalException instanceof Error &&
    !!(hint?.originalException as AxiosError)?.response;

  if (isApiError) {
    const status = (hint?.originalException as AxiosError)?.response?.status;

    // NOTE: 一部のエラー報告は除外する
    switch (status) {
      case HttpStatusCode.Unauthorized:
        return true;
      case HttpStatusCode.Forbidden:
        return true;
      case HttpStatusCode.NotFound:
        return true;
      case HttpStatusCode.UnprocessableEntity:
        return true;
      default:
        return false;
    }
  }

  return false;
};

const isSkipGrpcError = (hint: Sentry.EventHint | undefined) => {
  const isGrpcError =
    hint?.originalException instanceof Error &&
    !!(hint?.originalException as RpcError)?.code;

  if (isGrpcError) {
    const code = (hint?.originalException as RpcError)?.code;

    // NOTE: 一部のエラー報告は除外する
    switch (code) {
      case StatusCode.NOT_FOUND:
        return true;
      case StatusCode.PERMISSION_DENIED:
        return true;
      case StatusCode.UNAUTHENTICATED:
        return true;
      default:
        return false;
    }
  }

  return false;
};

export const setSentryUser = (): void => {
  const partnerUser = store.state.domains.user.partnerUser;
  if (partnerUser) {
    Sentry.setUser({
      "Partner User ID": partnerUser.id,
      "Partner ID": partnerUser.partner.id,
      "Partner Company ID": partnerUser.partner.company.id,
      "Partner Company Name": partnerUser.partner.company.name
    });
  }
};

export const removeSentryUser = (): void => {
  Sentry.configureScope(scope => scope.setUser(null));
};
